import { copyToClipboard as clipboardWrite } from "../../../services/Utils/ClipboardUtil";
import { Trans, t } from '@lingui/macro';
import { EventEmitter } from "events";
import hotkeys from "hotkeys-js";
import { ChevronRight, Loader2, Copy, Languages, FileText, List, CheckSquare, SpellCheck, HelpCircle, MessageSquare, MessageCircle, Bot } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Room } from "../../../models/Room";
import { RoomItem } from "../../../models/RoomItem";
import { Fire } from "../../../services/FireClient";
import { askAI } from "../../../services/AiMemoAnalyzer";
import { My } from "../../../services/My";
import { UtilApp } from "../../../services/Utils/UtilApp";
import { MemoModalEmitter } from "./PanelMemos";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../../components/ui/dialog";
import { Button } from "../../../components/ui/button";
import { Textarea } from "../../../components/ui/textarea";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui/select";
import { cn } from "../../../lib/utils";
import { useResponsive } from "../../../hooks/useResponsive";

export class TextAssistantModalCtrl {
  static Emitter = new EventEmitter();
  static room: Room | null = null;
  static text: string = '';
  static memos: RoomItem[] = [];

  static open(room: Room | null, text: string, memos: RoomItem[] | null) {
    this.room = room;
    this.text = text;
    this.memos = memos || [];
    this.Emitter.emit('open');
  }

  static clear() {
    this.room = null;
    this.text = '';
    this.memos = [];
  }
}

export const TextAssistantModal: React.FC = () => {
  const [inputText, setInputText] = useState('');
  const [prompt, setPrompt] = useState('');
  const [outputText, setOutputText] = useState('');
  const [isPrompting, setIsPrompting] = useState(false);
  const [show, setShow] = useState(false);
  const { isMobile } = useResponsive();

  // Ref for focus
  const promptInputRef = useRef<HTMLInputElement>(null);

  const prompts = [
    { text: t`Translate this text to ${UtilApp.getCurrentLanguage()}.`, label: t`Translate`, icon: Languages },
    { text: t`Summarize this text to a short paragraph.`, label: t`Summarize`, icon: FileText },
    { text: t`List the key points from this text.`, label: t`List`, icon: List },
    { text: t`Extract tasks from this text.`, label: t`Tasks`, icon: CheckSquare },
    { text: t`Rewrite this text to with correct grammar.`, label: t`Grammar`, icon: SpellCheck },
    { text: t`Create questions from this text.`, label: t`Questions`, icon: HelpCircle },
    { text: t`Create questions and answers with this text.`, label: t`Q&A`, icon: MessageSquare },
    { text: t`Create possible answers to this text.`, label: t`Answers`, icon: MessageCircle },
  ];

  const room = TextAssistantModalCtrl.room;
  const text = TextAssistantModalCtrl.text;
  const memos = TextAssistantModalCtrl.memos;

  const roomName = room ? Room.getRoomNameWithSharp(room, My.user.id) : '';

  useEffect(() => {
    hotkeys('shift+x', function (event, handler) {
      event.preventDefault();
      setShow(true);
    });
    TextAssistantModalCtrl.Emitter.on('open', () => {
      setShow(true);
    });

    const handleTextAssistant = (text: string, roomId?: string) => {
      const isRoomPageOpened = window.location.pathname.includes('/channel/') || window.location.pathname.includes('/room/');
      if (isMobile || !isRoomPageOpened) {
        const room = roomId ? My.Rooms.get(roomId) : null;
        TextAssistantModalCtrl.open(room ?? null, text, []);
      }
    };

    MemoModalEmitter.on('text-assistant', handleTextAssistant);

    return () => {
      hotkeys.unbind('shift+x');
      TextAssistantModalCtrl.Emitter.removeAllListeners('open');
      MemoModalEmitter.removeListener('text-assistant', handleTextAssistant);
    }
  }, [isMobile])

  useEffect(() => {
    if (show) {
      onPresent();
    }
  }, [show]);

  async function onPresent() {
    setInputText(TextAssistantModalCtrl.text.trim());
    setOutputText('');
  }

  function dismiss() {
    setShow(false);
    TextAssistantModalCtrl.clear();
  }

  function changePrompt(str: string) {
    setPrompt(str);
    focusOnPromptInput();
  }

  async function runPrompt() {
    if (isPrompting)
      return;
    setIsPrompting(true);
    const message = `${prompt} \n\n${inputText}`
    try {
      const answer = await askAI(message);
      setOutputText(answer.trim());
    } catch (error: any) {
      alert(error.message);
    }
    setIsPrompting(false);
  }

  function createNew() {
    MemoModalEmitter.emit('create', getTextAsHtml());
  }

  function addToMemo(itemId: string) {
    MemoModalEmitter.emit('add', itemId, getTextAsHtml());
  }

  function getTextAsHtml() {
    return outputText.replaceAll('\n', '<br />');
  }

  async function copyToClipboard(text: string) {
    await clipboardWrite(text);
    dismiss();
  }

  function focusOnPromptInput() {
    setTimeout(() => {
      promptInputRef.current?.focus();
    }, 200);
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      runPrompt();
    }
  };

  return (
    <Dialog open={show} onOpenChange={(open) => !open && dismiss()}>
      <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col p-6 overflow-hidden focus:outline-none">
        <DialogHeader className="pb-4 border-b">
          <DialogTitle className="text-xl font-bold flex items-center gap-2">
            <Bot className="w-6 h-6 text-primary" />
            <Trans>Text Assistant</Trans>
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto custom-scrollbar space-y-6 pt-4">
          {/* Input Section */}
          <div className="space-y-4">
            <Label className="text-sm font-medium text-muted-foreground uppercase tracking-wider"><Trans>Input</Trans></Label>
            <Textarea
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              disabled={isPrompting}
              className="min-h-[120px] resize-none"
              placeholder={t`Enter text here...`}
            />

            {/* Prompt Shortcut Buttons */}
            {isMobile ? (
              <Select onValueChange={changePrompt} value={prompt}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder={t`Select a prompt...`} />
                </SelectTrigger>
                <SelectContent>
                  {prompts.map((p) => (
                    <SelectItem key={p.label} value={p.text}>
                      <div className="flex items-center gap-2">
                        <p.icon className="w-4 h-4" />
                        <span>{p.label}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {prompts.map((p) => {
                  const Icon = p.icon;
                  return (
                    <Button
                      key={p.label}
                      variant={prompt === p.text ? "default" : "outline"}
                      size="sm"
                      onClick={() => changePrompt(p.text)}
                      className={cn(
                        "justify-start h-9 px-3",
                        prompt === p.text ? "bg-primary/10 text-primary hover:bg-primary/20 hover:text-primary border-primary/50 shadow-none" : "hover:bg-muted/50 text-[color:rgb(var(--text-body))] bg-background"
                      )}
                    >
                      <Icon className={cn("w-4 h-4 mr-2", prompt === p.text ? "text-primary" : "text-muted-foreground")} />
                      <span className="truncate">{p.label}</span>
                    </Button>
                  )
                })}
              </div>
            )}

            <div className="flex gap-2 items-center">
              <Input
                ref={promptInputRef}
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                onKeyDown={handleKeyDown}
                disabled={!inputText}
                placeholder={t`Type a prompt or select one above`}
                className="flex-1 h-10"
              />
              <Button
                onClick={runPrompt}
                disabled={!prompt || isPrompting}
                className="h-10 px-6 font-medium"
              >
                {isPrompting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                <Trans>Run Prompt</Trans>
              </Button>
            </div>
          </div>

          {/* Output Section */}
          {outputText && (
            <div className="space-y-4 pt-6 border-t animate-in fade-in slide-in-from-bottom-2 duration-300">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-medium text-muted-foreground uppercase tracking-wider"><Trans>Output</Trans></Label>
                <Button variant="outline" size="sm" onClick={() => copyToClipboard(outputText)} className="h-8">
                  <Copy className="w-3.5 h-3.5 mr-2" />
                  <Trans>Copy and close</Trans>
                </Button>
              </div>

              <Textarea
                value={outputText}
                onChange={(e) => setOutputText(e.target.value)}
                disabled={isPrompting}
                className="min-h-[200px]"
              />

              {room && (
                <div className="pt-4 space-y-3">
                  <div className="flex items-center gap-2 py-2">
                    <Label className="font-medium"><Trans>Add to Memo</Trans></Label>
                    <div className="h-px bg-border flex-1"></div>
                    <small className="text-muted-foreground">{roomName}</small>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    <Button variant="secondary" className="justify-between h-auto py-3 px-4 bg-muted/50 hover:bg-muted border border-transparent hover:border-border" onClick={createNew}>
                      <span className="font-medium"><Trans>Create New Memo</Trans></span>
                      <ChevronRight className="w-4 h-4 text-muted-foreground" />
                    </Button>
                    {memos.map(item => (
                      <Button key={item.id} variant="secondary" className="justify-between h-auto py-3 px-4 bg-muted/50 hover:bg-muted border border-transparent hover:border-border" onClick={() => addToMemo(item.id)}>
                        <span className="truncate">{item.title}</span>
                        <ChevronRight className="w-4 h-4 text-muted-foreground" />
                      </Button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default TextAssistantModal;
